import { useEffect, useState, useCallback, useRef } from 'react';
import Flickity from 'flickity';

const useHorizontalSlides = (slides, setShowEnd, params, startAtBeginning) => {
  const initialIndex = startAtBeginning ? 0 : params.slide ? parseInt(params.slide) - 1 : 0;

  const [selectedIndex, setSelectedIndex] = useState(initialIndex);
  const [slideController, setSlideController] = useState(null);
  const [sliderReady, setSliderReady] = useState(false);
  const [nextActive, setNextActive] = useState(false);
  const [prevActive, setPrevActive] = useState(false);

  const initialSlideIndex = useRef(initialIndex);

  const updateData = useCallback((slider) => {
    const currentIndex = slider.selectedIndex;
    setSelectedIndex(currentIndex);

    const currentSlideData = slides[currentIndex];
    if (!currentSlideData) return;

    if (currentSlideData.type === 'decision') {
      setNextActive(false);
    } else {
      if (currentIndex + 1 === slides.length) {
        setNextActive(true);
      } else {
        setNextActive(slider.cells.length > currentIndex + 1);
      }
    }
    setPrevActive(currentIndex > 0);
  }, [slides]);

  useEffect(() => {
    const mainElem = document.querySelector('.main');
    if (!mainElem || !slides || slides.length === 0) return;

    // Initialize Flickity once per person/data load.
    // setGallerySize/adaptiveHeight disabled: all slides are height:100vh and CSS
    // controls all sizing — letting Flickity set inline heights fights the CSS and
    // causes reflows.
    const slider = new Flickity(mainElem, {
      initialIndex: initialSlideIndex.current,
      pageDots: false,
      draggable: false,
      prevNextButtons: false,
      setGallerySize: false,
      adaptiveHeight: false,
      percentPosition: true,
      watchCSS: false,
      selectedAttraction: 0.01,
      friction: 0.15
    });

    // Resize/reposition after layout settles, then reveal the slider.
    // Revealing AFTER the reposition prevents the 250ms jump from being visible.
    const timer = setTimeout(() => {
      slider.resize();
      slider.reposition();
      setSliderReady(true);
    }, 250);

    const handleResize = () => {
      slider.resize();
      slider.reposition();
    };

    window.addEventListener('resize', handleResize);

    slider.on('select', () => updateData(slider));

    setSlideController(slider);
    updateData(slider);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', handleResize);
      slider.off('select');
      slider.destroy();
      setSliderReady(false);
    };
  }, [slides, params.person, updateData]);

  const next = useCallback(() => {
    if (slideController) {
      if (selectedIndex + 1 >= slides.length) {
        setShowEnd(true);
      } else {
        slideController.next();
      }
    }
  }, [slideController, selectedIndex, slides.length, setShowEnd]);

  const prev = useCallback(() => {
    if (slideController) {
      slideController.previous();
    }
  }, [slideController]);

  return {
    nextSlide: next,
    previousSlide: prev,
    sliderReady,
    selectedIndex,
    nextActive,
    prevActive,
  };
};

export default useHorizontalSlides;